import React, { useState } from 'react';
import './FAQ.css';
import SEO from '../components/SEO';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  
  const faqs: FAQItem[] = [
    {
      question: 'What should I expect at the tour?',
      answer: 'When you visit our kitchen for a tour, expect questions from us! We\'re genuinely curious about you, your business, and how we can help you start and grow. Every entrepreneur is unique, and we want to address your specific needs and current challenges.'
    },
    {
      question: 'How much does it cost?',
      answer: 'No two businesses are alike, so we customize packages for each member. Our industry-leading "Pay For What You Use" philosophy and technology saves you money. Pricing fits your business—not the other way around. Schedule a tour or chat with us to learn how we can help your business succeed.'
    },
    {
      question: 'What\'s included in my membership?',
      answer: 'Your membership includes 24/7 kitchen access, a dedicated workstation (shareable to reduce costs), prep table, dry storage, designated cold storage, access to multiple sinks and shared work tables, remote online equipment booking, WiFi, mop and rag service, chemical sanitizing dishwasher, and basic kitchen consumables.'
    },
    {
      question: 'What equipment do I need to bring?',
      answer: 'We provide the storage space and heavy equipment. You bring your knives, bowls, blenders, whisks, hotel pans, and any specific tools required for your food preparation.'
    },
    {
      question: 'Do I need a health inspection?',
      answer: 'Yes. In Vancouver, Vancouver Coastal Health will review your food safety plan and inspect you at our kitchen. You\'ll interact directly with the inspector. Our kitchens have been inspected hundreds of times over the years and meet all facility requirements.'
    },
    {
      question: 'How flexible are the terms?',
      answer: 'While 12-month contracts are most cost-effective, we offer six-month, three-month, and month-to-month plans. Drop-in rates are also available.'
    },
    {
      question: 'What do I need to join Commissary Connect?',
      answer: 'You\'ll need a health authority inspection, liability insurance, a FoodSafe certificate, and a fully refundable damage deposit. We\'ll walk you through all the details during your tour and follow-up communications.'
    },
    {
      question: 'I want to start in a couple of months. Should I wait to sign a contract?',
      answer: 'We recommend signing up early. This gives you time to complete your food safety plan and schedule your health inspection. We\'re flexible with start dates and terms—just ask during your tour about available options.'
    },
    {
      question: 'What if I outgrow my customized package?',
      answer: 'Your success is our business! When you need more space, time, or equipment access, we\'ll work with you to find the most cost-effective way to grow. This includes potential scaling to our HACCP-ready shared-use facility—one of the first of its kind in Canada.'
    },
    {
      question: 'What if my business struggles?',
      answer: 'Commissary Connect lets you pursue your passion without risking your future. No five-year leases or expensive leasehold improvements—we offer terms that work for you. The food industry is challenging, and sometimes despite best efforts, goals aren\'t met. Keep communication open, and we\'ll work through it together. We\'re here for operational advice, connections, and support.'
    },
    {
      question: 'Can I use your kitchen while looking for my own space?',
      answer: 'Absolutely! We support your success on your terms. Whether you need our space temporarily or have specific goals, let us know. Your business goals become ours. We\'ll help you transition to a retail storefront or co-manufacturing facility. We also offer food cart and pop-up space rentals—test your concept before committing to long-term leases or expensive equipment.'
    },
    {
      question: 'What types of businesses use your kitchens?',
      answer: 'Our members include food trucks, caterers, meal prep services, bakeries, ghost kitchens, and CPG food manufacturers. If you\'re in the food business, we have a solution for you!'
    }
  ];
  
  const toggleFAQ = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };
  
  return (
    <div className="faq-page">
      {/* 769 impr at pos 10.0 with 0.39% CTR — informational intent the generic title does not answer */}
      <SEO
        title="Commissary Kitchen Rental FAQ | Pricing, Hours & Access"
        description="How commissary kitchen rental works: hourly pricing, 24/7 access, health approval, storage and what you need to bring. Answers before you book a tour."
        keywords="commissary kitchen pricing, kitchen rental cost, how commissary kitchens work"
      />
      <div className="container">
        <h1>Frequently Asked Questions</h1>
        <p className="page-subtitle">We understand that starting a business is challenging. Our goal is to assist you in every way we can. Here are the questions we hear most often.</p>
        
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div key={index} className={`faq-item ${activeIndex === index ? 'active' : ''}`}>
              <button 
                className="faq-question"
                onClick={() => toggleFAQ(index)}
              >
                <span>{faq.question}</span>
                <span className="faq-icon">{activeIndex === index ? '−' : '+'}</span>
              </button>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="faq-contact">
          <h2>Ready to Scale Your Food Business?</h2>
          <p className="cta-main">Join 500+ successful food entrepreneurs at Vancouver's premier shared-use facilities</p>
          
          <div className="cta-benefits">
            <span className="cta-benefit">Launch faster</span>
            <span className="cta-divider">•</span>
            <span className="cta-benefit">Scale smarter</span>
            <span className="cta-divider">•</span>
            <span className="cta-benefit">Pay only for what you use</span>
          </div>

          <a 
            href="/application" 
            data-apply-label="faq_apply"
            className="apply-btn" 
            onClick={() => {
              if (window.gtag) {
                window.gtag('event', 'click', {
                  event_category: 'Application',
                  event_label: 'FAQ Apply Now',
                  value: 1
                });
              }
            }}
          >
            Get Started Today
          </a>
          
          <div className="cta-contact-info">
            <a href="tel:7785587272" data-phone-label="faq_cta" className="cta-phone">778.558.7272</a>
            <span className="cta-separator">|</span>
            <a href="mailto:info@commissaryconnect.com" className="cta-email">info@commissaryconnect.com</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;